export type PanelMode = 'medicine' | 'grocery';

export type RoutePanelAffinity = 'medicine' | 'grocery' | 'shared' | 'unknown';

export const ACTIVE_PANEL_STORAGE_KEY = 'activePanel';

export const MEDICINE_PANEL_KEYS = [
  'dashboard',
  'quality-check',
  'medicines',
  'combos',
  'draft-medicines-request',
  'rejected',
  'categories',
  'sub-categories',
  'warning-tags',
  'dose-form',
  'category-dose-form-mapping',
] as const;

export const GROCERY_PANEL_KEYS = [
  'dashboard',
  'grocery-quality-check',
  'groceries',
  'grocery-rejected',
  'grocery-categories',
  'grocery-sub-categories',
] as const;

export const pathnameToMenuKey = (pathname: string): string => {
  const segment = pathname.split('/').filter(Boolean)[0]?.toLowerCase();
  if (!segment) return 'dashboard';
  return segment;
};

export const isMedicinePanelRoute = (pathname: string): boolean => {
  const key = pathnameToMenuKey(pathname);
  return (MEDICINE_PANEL_KEYS as readonly string[]).includes(key);
};

export const isGroceryPanelRoute = (pathname: string): boolean => {
  const key = pathnameToMenuKey(pathname);
  return (GROCERY_PANEL_KEYS as readonly string[]).includes(key);
};

/** Panel mode implied by the current path, or null for shared/unknown routes (e.g. `/`). */
export const getPanelModeFromPathname = (pathname: string): PanelMode | null => {
  const affinity = getRoutePanelAffinity(pathname);
  if (affinity === 'grocery' || affinity === 'medicine') return affinity;
  return null;
};

export const getRoutePanelAffinity = (
  pathname: string,
): RoutePanelAffinity => {
  const isMed = isMedicinePanelRoute(pathname);
  const isGroc = isGroceryPanelRoute(pathname);
  if (isMed && isGroc) return 'shared';
  if (isMed) return 'medicine';
  if (isGroc) return 'grocery';
  return 'unknown';
};

export const isPathAllowedInPanel = (
  pathname: string,
  mode: PanelMode,
): boolean => {
  const affinity = getRoutePanelAffinity(pathname);
  if (affinity === 'shared' || affinity === 'unknown') return true;
  return affinity === mode;
};

export const getDefaultRouteForPanel = (mode: PanelMode): string => '/';

export const getPathForMenuKey = (key: string): string =>
  key === 'dashboard' ? '/' : `/${key}`;

export const readStoredPanelMode = (): PanelMode | null => {
  const stored = localStorage.getItem(ACTIVE_PANEL_STORAGE_KEY);
  if (stored === 'medicine' || stored === 'grocery') return stored;
  return null;
};

export const writeStoredPanelMode = (mode: PanelMode) => {
  localStorage.setItem(ACTIVE_PANEL_STORAGE_KEY, mode);
};
